import BaseSwiper from './base-swiper.js';
import { pubsub } from '@services';

class ProductSwiper extends BaseSwiper {
  /** @type {HTMLElement[]} */ #allSlides = [...this.querySelectorAll('.swiper-slide')];

  /** @type {HTMLElement | import('swiper').Swiper | null} */ #imageSwiper = this.closest(
    '.shopify-section'
  )?.querySelector('.swiper [data-pin-container="image"]')
    ? this.closest('.shopify-section')?.querySelector('.swiper:has([data-pin-container="image"])')
    : null;

  connectedCallback() {
    super.connectedCallback();
    this.#watchImageSwiper();
  }

  /**
   * Subscribe to the image swiper init event and bind slide change handling.
   * @returns {void}
   */
  #watchImageSwiper() {
    if (
      this.querySelectorAll('product-pin').length &&
      this.#allSlides.length &&
      this.#imageSwiper
    ) {
      pubsub.subscribe('swiper:init', (/** @type {import('swiper').Swiper} */ swiper) => {
        if (swiper.el === this.#imageSwiper) {
          this.#imageSwiper = swiper;
          this.#updateSlides();
          this.#imageSwiper.on('slideChange', this.#onImageSwiperSlideChange.bind(this));
        }
      });
    }
  }

  /**
   * Handle slide change event on the image swiper.
   * @returns {void}
   */
  #onImageSwiperSlideChange() {
    this.#updateSlides();
  }

  /**
   * Add or remove product swiper slides based on which product pins are visible
   * in the currently active image swiper slide.
   * @returns {void}
   */
  #updateSlides() {
    const activeSlide = this.#imageSwiper.slides[this.#imageSwiper.activeIndex];
    if (!activeSlide) return;

    /** @type {(string | null)[]} */
    const imagePinsIDs = [...activeSlide.querySelectorAll('product-pin')]?.map((pin) =>
      pin.getAttribute('data-product-id')
    );

    this.#allSlides.forEach((slide, index) => {
      /** @type {(string | null)[]} */
      const slidePinsIDs = [...slide.querySelectorAll('product-pin')]?.map((pin) =>
        pin.getAttribute('data-product-id')
      );

      const isInSwiper = this.swiper.slides.includes(slide);
      const hasImagePin = imagePinsIDs.some((id) => slidePinsIDs.includes(id));

      /**
       * CONDITION A: Slide is currently in this.swiper.slides
       * 1. If slide doesn't have a pin from imagePinsIDs, remove slide
       * 2. If slide has a pin from imagePinsIDs, do nothing
       *
       * CONDITION B: Slide is not currently in this.swiper.slides
       * 1. If slide doesn't have a pin from imagePinsIDs, do nothing
       * 2. If slide has a pin from imagePinsIDs, add slide
       */
      if (!isInSwiper && hasImagePin) {
        this.swiper.addSlide(index, slide);
      } else if (isInSwiper && !hasImagePin) {
        const realIndex = this.swiper.slides.indexOf(slide);
        this.swiper.removeSlide(realIndex);
      }
    });
  }
}

if (!customElements.get('product-swiper')) {
  customElements.define('product-swiper', ProductSwiper);
}
